// react
import { Suspense, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link, useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

// material ui
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { energyAnalysisApiSlice } from '@/api/apiSlice';
import { useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import { useRemoveAddressInfoForMeterMeterAddressAnalysisTypeAccountIdMeterIdDeleteMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';
import { useRemovePmmMeterPmmMeterAccountIdUnitIdDeleteMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisPMM';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import { WritableOnly } from '@/components/access-control';
import { DeleteButton, EditButtonLink } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/dialog/ConfirmationDialog';
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

// feature-components
import LinkedEnergyGroupsCard from '@/features/energyxplorer/components/energy-explorer-link/LinkedEnergyGroupsCard';

// components
import BuildingInfoSubheader from '@/features/energyxplorer/portfolio/components/meter-drawer/BuildingInfoSubheader';
import BuildingInfoTab from '@/features/energyxplorer/portfolio/components/meter-drawer/BuildingInfoTab';
import MeterChartPanel from '@/features/energyxplorer/portfolio/components/meter-drawer/MeterChartPanel';
import MeterDisplayName from '@/features/energyxplorer/portfolio/components/meter-drawer/MeterDisplayName';
import MeterEditPanel from '@/features/energyxplorer/portfolio/components/meter-drawer/MeterEditPanel';
import MeterTypeIcon from '@/features/energyxplorer/portfolio/components/meter-drawer/MeterTypeIcon';
import EditMatrikelBuildingForm from '@/features/energyxplorer/portfolio/forms/EditMatrikelBuildingForm';
import useMatrikelList from '@/features/energyxplorer/portfolio/hooks/useMatrikelList';
import {
  getMeterDisplayAddress,
  getMeterId,
} from '@/features/energyxplorer/portfolio/utils/meterUtils';
import type {
  AnalysisType,
  EnrichedMeter,
  PortfolioMeter,
} from '@/features/energyxplorer/types';
import { useHasService } from '@/hooks/useHasService';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';

const ICON_BY_TYPE: Record<string, React.ReactElement> = {
  power: <BoltRoundedIcon color="power" fontSize="small" />,
  water: <WaterDropIcon color="water" fontSize="small" />,
  heat: <LocalFireDepartmentIcon color="heat" fontSize="small" />,
};

/** Find which tab index corresponds to a specific meter in the tab list */
function findMeterTabIndex(
  tabs: Array<{ kind: string; meter?: Record<string, unknown> }>,
  meter: Record<string, unknown>,
): number {
  const meterPointId = meter?.metering_point_id ?? meter?.MeteringPointId;
  const unitId =
    meter?.UnitId ??
    (meter?.data_stream as Record<string, unknown> | undefined)?.unit_id;

  const idx = tabs.findIndex(
    (tab) =>
      tab.kind === 'meter' &&
      tab.meter &&
      ((meterPointId &&
        (tab.meter.metering_point_id === meterPointId ||
          tab.meter.MeteringPointId === meterPointId)) ||
        (unitId &&
          (tab.meter.UnitId === unitId ||
            (tab.meter.data_stream as Record<string, unknown> | undefined)
              ?.unit_id === unitId))),
  );
  return idx >= 0 ? idx : 0;
}

interface MeterDrawerProps {
  open: boolean;
  onClose: () => void;
  meter: EnrichedMeter & Record<string, unknown>;
  meterType: 'meter' | 'matrikel';
  analysisType: AnalysisType;
}

const MeterDrawer = ({
  open,
  onClose,
  meter,
  meterType,
  analysisType,
}: MeterDrawerProps) => {
  const { t } = useTranslation([
    `portfolio-analysis_${analysisType}`,
    'common',
    'energyxplorer',
  ]);
  console.log('Meter opened', meter);
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountId } = useParams({ strict: false });
  const { matrikel_list, isFetching: isMatrikelListFetching } =
    useMatrikelList();

  const [removeMeterFromMatrikel] =
    useRemoveAddressInfoForMeterMeterAddressAnalysisTypeAccountIdMeterIdDeleteMutation();
  const [removePmmMeter] =
    useRemovePmmMeterPmmMeterAccountIdUnitIdDeleteMutation();
  const [updatePortfolioAnalysis] =
    useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation();

  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    accountId,
    {
      skip: !accountId,
    },
  ) as {
    data:
      | Array<{
          UnitId: number;
          UnitType?: { UnitTypeId: number };
          [key: string]: unknown;
        }>
      | undefined;
  };

  const pmmUnitIds = useMemo(() => {
    const ids = new Set<string>();
    if (Array.isArray(units)) {
      units.forEach((u) => {
        if (u.UnitType?.UnitTypeId === 30) ids.add(String(u.UnitId));
      });
    }
    return ids;
  }, [units]);

  const handleRemoveMeterFromMatrikel = async (m: PortfolioMeter) => {
    const unitId = m.UnitId ? String(m.UnitId) : null;
    const meteringPointId = m.MeteringPointId
      ? String(m.MeteringPointId)
      : null;
    const remoniUnitId = m.ReMoniUnitId ? String(m.ReMoniUnitId) : null;
    const isPmm =
      (unitId && pmmUnitIds.has(unitId)) ||
      (meteringPointId && pmmUnitIds.has(meteringPointId)) ||
      (remoniUnitId && pmmUnitIds.has(remoniUnitId));

    let hasError = false;
    if (isPmm) {
      let pmMeterType: 'solar_panels' | 'heat_pump' | 'power_meter';
      if (m.type_of_mp === 'PV') pmMeterType = 'solar_panels';
      else if (m.type === 'heat') pmMeterType = 'heat_pump';
      else pmMeterType = 'power_meter';
      const actualUnitId = unitId || meteringPointId || remoniUnitId;
      const result = await removePmmMeter({
        accountId: Number(accountId),
        unitId: Number(actualUnitId),
        meterType: pmMeterType,
      });
      if ('error' in result) hasError = true;
      else
        dispatch(
          energyAnalysisApiSlice.util.invalidateTags(['PortfolioAnalysis']),
        );
    } else {
      const result = await removeMeterFromMatrikel({
        meterId: getMeterId(m),
        analysisType: m.type as AnalysisType,
        accountId: accountId,
      });
      if ('error' in result) hasError = true;
    }
    if (!hasError) {
      await updatePortfolioAnalysis({ accountId });
      dispatch(
        showSnackbar({
          message:
            t('matrikel.meter_removed_success', { ns: 'energyxplorer' }) ||
            'Meter removed successfully',
          severity: 'success',
        }),
      );
    }
  };

  const matrikelKey = (meter?.matrikel_key as string | undefined) ?? '';

  const matrikelItem = useMemo(
    () =>
      matrikel_list?.find((item) => item.metadata?.MatrikelKey === matrikelKey),
    [matrikel_list, matrikelKey],
  );

  const metersByType = useMemo(() => {
    if (!matrikelItem) return { power: [], water: [], heat: [] };

    const toFlat = (
      list: typeof matrikelItem.power.meter_list,
      type: AnalysisType,
    ): Record<string, unknown>[] =>
      (list
        ?.filter((m) => m?.metadata && (m?.hasAnalysis || m.metadata.UnitId))
        .map((m) => ({ ...m.metadata, ...m.analysis, type })) ?? []) as Record<
        string,
        unknown
      >[];

    return {
      power: toFlat(matrikelItem.power?.meter_list, 'power'),
      water: toFlat(matrikelItem.water?.meter_list, 'water'),
      heat: toFlat(matrikelItem.heat?.meter_list, 'heat'),
    };
  }, [matrikelItem]);

  const linkedEnergyGroups = (meter?.linked_energy_groups as unknown[]) ?? [];

  type TabDef =
    | { kind: 'building' }
    | {
        kind: 'meter';
        meter: Record<string, unknown>;
        analysisType: AnalysisType;
      };

  const tabs: TabDef[] = useMemo(() => {
    const result: TabDef[] = [{ kind: 'building' }];
    metersByType.power.forEach((m) =>
      result.push({ kind: 'meter', meter: m, analysisType: 'power' }),
    );
    metersByType.water.forEach((m) =>
      result.push({ kind: 'meter', meter: m, analysisType: 'water' }),
    );
    metersByType.heat.forEach((m) =>
      result.push({ kind: 'meter', meter: m, analysisType: 'heat' }),
    );
    return result;
  }, [metersByType]);

  const initialTab = useMemo(
    () => (meterType === 'matrikel' ? 0 : findMeterTabIndex(tabs, meter)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meterType, tabs.length, meter],
  );

  const [activeTab, setActiveTab] = useState(initialTab);
  const [meterInEditMode, setMeterInEditMode] = useState(false);
  const [editBuildingOpen, setEditBuildingOpen] = useState(false);
  const [confirmMeter, setConfirmMeter] = useState<PortfolioMeter | null>(null);
  const [energyGroupsAnchor, setEnergyGroupsAnchor] =
    useState<HTMLElement | null>(null);
  const [editBuildingFormDirty, setEditBuildingFormDirty] = useState(false);
  const [confirmLeaveEditBuilding, setConfirmLeaveEditBuilding] =
    useState(false);
  const [editPanelDirty, setEditPanelDirty] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState<number | null>(null);

  const requestCloseEditBuilding = () => {
    if (editBuildingFormDirty) {
      setConfirmLeaveEditBuilding(true);
    } else {
      setEditBuildingOpen(false);
    }
  };

  const handleTabChange = (_: unknown, newValue: number) => {
    if (meterInEditMode && editPanelDirty) {
      setPendingTabIndex(newValue);
    } else {
      setActiveTab(newValue);
      setMeterInEditMode(false);
    }
  };

  const activeTabDef = tabs[activeTab] ?? tabs[0];

  const matrikelAddress =
    matrikelItem?.metadata?.Address ??
    matrikelItem?.metadata?.MainAddress ??
    (meter?.main_address as string | undefined);

  const drawerTitle =
    matrikelAddress ?? (meter?.address as string | undefined) ?? '';

  const hasDeepDiveService = useHasService('DeepDive');

  return (
    <>
      <InsetDrawer
        anchor="right"
        open={open}
        onClose={onClose}
        width={1500}
        headerActions={
          <>
            <WritableOnly>
              <Tooltip
                title={t('matrikel.edit_building', {
                  ns: 'energyxplorer',
                  defaultValue: 'Edit building',
                })}
              >
                <EditButtonLink
                  size="small"
                  onClick={() => setEditBuildingOpen(true)}
                  aria-label="edit building"
                >
                  {t('matrikel.edit_building', {
                    ns: 'energyxplorer',
                    defaultValue: 'Edit building',
                  })}
                </EditButtonLink>
              </Tooltip>
            </WritableOnly>
            <Tooltip title={t('drawer.cards.buildingData.bbrLinkTooltip')}>
              <Link
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
                to={`https://kort.bbr.dk/?bfe=${meter?.BFENummer}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconButton sx={{ borderRadius: 1 }}>
                  <OpenInNewRoundedIcon />
                </IconButton>
              </Link>
            </Tooltip>
          </>
        }
        subheader={<BuildingInfoSubheader matrikelKey={matrikelKey} />}
        title={
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <ApartmentRoundedIcon fontSize="small" />
            <Typography variant="h6" fontWeight={700} noWrap>
              {drawerTitle}
            </Typography>

            {hasDeepDiveService && (
              <Box sx={{ ml: 'auto' }}>
                <Chip
                  size="small"
                  label="DeepDive"
                  icon={
                    linkedEnergyGroups.length > 0 ? (
                      <CheckCircleIcon
                        fontSize="small"
                        sx={{ color: 'success.main !important' }}
                      />
                    ) : (
                      <AddCircleOutlineIcon fontSize="small" />
                    )
                  }
                  onClick={
                    linkedEnergyGroups.length > 0
                      ? (e) => setEnergyGroupsAnchor(e.currentTarget)
                      : () =>
                          navigate({
                            to: `/${accountId}/energyxplorer/deepdive/create`,
                          })
                  }
                  variant="outlined"
                  color={linkedEnergyGroups.length > 0 ? 'success' : 'default'}
                />
              </Box>
            )}
          </Box>
        }
      >
        <Box sx={{ display: 'flex', height: '100%', gap: 2 }}>
          {/* Vertical tab list */}
          <Tabs
            orientation="vertical"
            value={Math.min(activeTab, tabs.length - 1)}
            onChange={handleTabChange}
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              minWidth: 160,

              maxWidth: 200,
              '& .MuiTab-root': {
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
                minHeight: 48,
                px: 1.5,
                fontSize: '0.8rem',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
              '& .Mui-selected': {
                backgroundColor: 'action.selected',
              },
            }}
          >
            <Tab
              icon={<ApartmentRoundedIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
              iconPosition="start"
              label={t('matrikel.building_information', {
                ns: 'energyxplorer',
              })}
            />

            {isMatrikelListFetching &&
              tabs.filter((t) => t.kind === 'meter').length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={18} />
                </Box>
              )}

            {metersByType.power.map((m, i) => (
              <Tab
                key={`power-${i}`}
                icon={
                  m.type_of_mp === 'PV' ? (
                    <SolarPowerRoundedIcon color="power" fontSize="small" />
                  ) : (
                    ICON_BY_TYPE.power
                  )
                }
                sx={{
                  textTransform: 'none',
                }}
                iconPosition="start"
                label={
                  (m.DisplayName ||
                    m.display_name ||
                    m.Name ||
                    m.Address ||
                    m.UnitId) as string
                }
              />
            ))}

            {metersByType.water.map((m, i) => (
              <Tab
                key={`water-${i}`}
                icon={ICON_BY_TYPE.water}
                iconPosition="start"
                label={
                  (m.DisplayName ||
                    m.display_name ||
                    m.Name ||
                    m.Address ||
                    m.UnitId) as string
                }
                sx={{
                  textTransform: 'none',
                }}
              />
            ))}
            {metersByType.heat.map((m, i) => (
              <Tab
                key={`heat-${i}`}
                icon={ICON_BY_TYPE.heat}
                iconPosition="start"
                label={
                  (m.DisplayName ||
                    m.display_name ||
                    m.Name ||
                    m.Address ||
                    m.UnitId) as string
                }
                sx={{
                  textTransform: 'none',
                }}
              />
            ))}
          </Tabs>
          <Suspense>
            {/* Tab content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {activeTabDef?.kind === 'building' && (
                <BuildingInfoTab
                  metersByType={metersByType}
                  onNavigateToMeter={(m) =>
                    setActiveTab(findMeterTabIndex(tabs, m))
                  }
                  isLoading={
                    isMatrikelListFetching &&
                    tabs.filter((tab) => tab.kind === 'meter').length === 0
                  }
                />
              )}

              {activeTabDef?.kind === 'meter' && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <MeterTypeIcon
                        type={
                          activeTabDef.meter.type_of_mp === 'PV'
                            ? 'solar'
                            : activeTabDef.analysisType
                        }
                        sx={{}}
                      />
                      <Box>
                        <MeterDisplayName
                          recalcAccountId={account?.AccountId}
                          meterId={
                            (activeTabDef.meter.MeteringPointId ??
                              activeTabDef.meter.metering_point_id ??
                              activeTabDef.meter.UnitId) as string | number
                          }
                          address={
                            (activeTabDef.meter.Address ??
                              activeTabDef.meter.main_address) as
                              string | null | undefined
                          }
                          analysisType={activeTabDef.analysisType}
                          displayName={
                            (activeTabDef.meter.DisplayName ||
                              activeTabDef.meter.display_name ||
                              activeTabDef.meter.Name) as
                              string | null | undefined
                          }
                        />
                        <Typography variant="body2" color="text.secondary">
                          {getMeterDisplayAddress(activeTabDef.meter)}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <WritableOnly>
                        <EditButtonLink
                          size="small"
                          onClick={() => setMeterInEditMode(true)}
                          aria-label="edit meter"
                        >
                          {t('matrikel.edit_meter', {
                            ns: 'energyxplorer',
                            defaultValue: 'Edit meter',
                          })}
                        </EditButtonLink>

                        {activeTabDef.analysisType !== 'power' && (
                          <DeleteButton
                            size="small"
                            onClick={() =>
                              setConfirmMeter(
                                activeTabDef.meter as unknown as PortfolioMeter,
                              )
                            }
                            aria-label="delete meter"
                          >
                            {t('matrikel.remove_meter', {
                              ns: 'energyxplorer',
                              defaultValue: 'Remove meter',
                            })}{' '}
                          </DeleteButton>
                        )}
                      </WritableOnly>
                    </Stack>
                  </Box>
                  {meterInEditMode ? (
                    <Suspense>
                      <MeterEditPanel
                        key={String(
                          activeTabDef.meter.metering_point_id ??
                            activeTabDef.meter.MeteringPointId ??
                            activeTabDef.meter.UnitId,
                        )}
                        meter={activeTabDef.meter}
                        analysisType={activeTabDef.analysisType}
                        showOccupancy={
                          activeTabDef.meter.type_of_mp === 'PV' ? true : false
                        }
                        onBack={() => setMeterInEditMode(false)}
                        onDirtyChange={setEditPanelDirty}
                      />
                    </Suspense>
                  ) : (
                    <MeterChartPanel
                      key={String(
                        activeTabDef.meter.metering_point_id ??
                          activeTabDef.meter.MeteringPointId ??
                          activeTabDef.meter.UnitId,
                      )}
                      meter={activeTabDef.meter}
                      analysisType={activeTabDef.analysisType}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Suspense>
        </Box>
      </InsetDrawer>
      <Popover
        open={Boolean(energyGroupsAnchor)}
        anchorEl={energyGroupsAnchor}
        onClose={() => setEnergyGroupsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper sx={{ p: 1, maxWidth: 400 }}>
          <LinkedEnergyGroupsCard linkedEnergyGroups={linkedEnergyGroups} />
        </Paper>
      </Popover>
      <Suspense>
        <Dialog
          open={editBuildingOpen}
          onClose={requestCloseEditBuilding}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t('matrikel.edit_building', {
              ns: 'energyxplorer',
              defaultValue: 'Edit building',
            })}
          </DialogTitle>
          <DialogContent>
            {account?.AccountId && matrikelKey && (
              <EditMatrikelBuildingForm
                accountId={account.AccountId}
                matrikelKey={matrikelKey}
                onSuccess={() => setEditBuildingOpen(false)}
                onCancel={requestCloseEditBuilding}
                onDirtyChange={setEditBuildingFormDirty}
              />
            )}
          </DialogContent>
        </Dialog>
      </Suspense>
      <Suspense>
        <ConfirmationDialog
          open={confirmMeter !== null}
          variant="delete"
          message={t('matrikel.remove_meter_confirm', { ns: 'energyxplorer' })}
          onConfirm={() => {
            if (confirmMeter) handleRemoveMeterFromMatrikel(confirmMeter);
            setConfirmMeter(null);
          }}
          onClose={() => setConfirmMeter(null)}
        />
      </Suspense>
      <ConfirmationDialog
        open={confirmLeaveEditBuilding}
        variant="confirm"
        message={t('matrikel.unsaved_changes_confirm', {
          ns: 'energyxplorer',
          defaultValue:
            'You have unsaved changes. Are you sure you want to leave?',
        })}
        onConfirm={() => {
          setConfirmLeaveEditBuilding(false);
          setEditBuildingOpen(false);
          setEditBuildingFormDirty(false);
        }}
        onClose={() => setConfirmLeaveEditBuilding(false)}
      />
      <ConfirmationDialog
        open={pendingTabIndex !== null}
        variant="confirm"
        message={t('matrikel.unsaved_changes_confirm', {
          ns: 'energyxplorer',
          defaultValue:
            'You have unsaved changes. Are you sure you want to leave?',
        })}
        onConfirm={() => {
          if (pendingTabIndex !== null) {
            setActiveTab(pendingTabIndex);
            setMeterInEditMode(false);
            setEditPanelDirty(false);
          }
          setPendingTabIndex(null);
        }}
        onClose={() => setPendingTabIndex(null)}
      />
    </>
  );
};

export default MeterDrawer;
